import { useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useNavigationBlocker } from "@/context/NavigationBlockerContext";

/**
 * Hook to warn users about unsaved changes when leaving the page
 * Uses NavigationBlockerContext to intercept all navigation types
 * @param {boolean} hasUnsavedChanges - Whether there are unsaved changes
 * @param {string} message - Warning message for beforeunload event
 * @returns {Object} - isBlocked state and navigation control functions
 */
export default function useUnsavedChangesWarning(hasUnsavedChanges, message = "") {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isBlocked,
    pendingNavigation,
    setHasUnsavedChanges,
    setIsBlocked,
    setPendingNavigation,
  } = useNavigationBlocker();

  // Sync local hasUnsavedChanges with context
  useEffect(() => {
    setHasUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, setHasUnsavedChanges]);

  // Handle browser/tab close with beforeunload
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges, message]);

  // Intercept browser back/forward navigation
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handlePopState = () => {
      // Push the current location back to prevent navigation
      globalThis.history.pushState(null, "", location.pathname + location.search);
      setPendingNavigation({ path: "", type: "popstate" });
      setIsBlocked(true);
    };

    // Push current state to enable popstate detection
    globalThis.history.pushState(null, "", location.pathname + location.search);
    globalThis.addEventListener("popstate", handlePopState);

    return () => {
      globalThis.removeEventListener("popstate", handlePopState);
    };
  }, [hasUnsavedChanges, location.pathname, location.search, setPendingNavigation, setIsBlocked]);

  // Function to proceed with navigation
  const proceed = useCallback(() => {
    setIsBlocked(false);
    setHasUnsavedChanges(false);

    if (pendingNavigation) {
      if (pendingNavigation.type === "popstate") {
        globalThis.history.go(-1);
      } else if (pendingNavigation.path) {
        navigate(pendingNavigation.path);
      }
      setPendingNavigation(null);
    }
  }, [pendingNavigation, navigate, setIsBlocked, setHasUnsavedChanges, setPendingNavigation]);

  // Function to cancel navigation
  const reset = useCallback(() => {
    setIsBlocked(false);
    setPendingNavigation(null);
  }, [setIsBlocked, setPendingNavigation]);

  return {
    isBlocked,
    proceed,
    reset,
  };
}
